import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"
import {
  appendOrganizationBrandIds,
  appendOrganizationClientIds,
  organizationClientIdsKey,
} from "../../lib/utils/organizationReportClientIds"

function pagePropertyOutliersKey({ organizationId, startDate, endDate, clientIds, brandIds } = {}) {
  return `${String(organizationId ?? "")}|${String(startDate ?? "")}|${String(endDate ?? "")}|${organizationClientIdsKey(clientIds)}|${organizationClientIdsKey(brandIds)}`
}

/**
 * GET /api/v1/report/organizations/{organizationId}/page/property-outliers?start_date=&end_date=&client_ids=&brand_ids=
 */
export const fetchOrganizationPagePropertyOutliers = createAsyncThunk(
  "organizationPagePropertyOutliers/fetch",
  async ({ organizationId, startDate, endDate, clientIds, brandIds } = {}, { rejectWithValue }) => {
    try {
      if (organizationId == null || organizationId === "") {
        return rejectWithValue("organizationId is required")
      }
      if (!startDate || !endDate) {
        return rejectWithValue("start_date and end_date are required")
      }

      const query = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
      })
      appendOrganizationClientIds(query, clientIds)
      appendOrganizationBrandIds(query, brandIds)
      const data = await get(
        `report/organizations/${organizationId}/page/property-outliers?${query}`,
      )

      return {
        data,
        organizationId,
        startDate,
        endDate,
        fetchKey: pagePropertyOutliersKey({ organizationId, startDate, endDate, clientIds, brandIds }),
      }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch organization property outliers")
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg?.force) return true
      const key = pagePropertyOutliersKey(arg)
      const state = getState().organizationPagePropertyOutliers
      if (state?.status === "loading" && state?.requestedFetchKey === key) return false
      if (state?.status === "succeeded" && state?.fetchedFetchKey === key) return false
      return true
    },
  },
)

const initialState = {
  data: null,
  status: "idle",
  error: null,
  organizationId: null,
  startDate: null,
  endDate: null,
  requestedFetchKey: null,
  fetchedFetchKey: null,
}

const organizationPagePropertyOutliersSlice = createSlice({
  name: "organizationPagePropertyOutliers",
  initialState,
  reducers: {
    clearOrganizationPagePropertyOutliers: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganizationPagePropertyOutliers.pending, (state, action) => {
        const arg = action.meta.arg || {}
        state.status = "loading"
        state.error = null
        state.requestedFetchKey = pagePropertyOutliersKey(arg)
      })
      .addCase(fetchOrganizationPagePropertyOutliers.fulfilled, (state, action) => {
        if (action.payload?.fetchKey !== state.requestedFetchKey) return
        state.status = "succeeded"
        state.data = action.payload?.data ?? null
        state.organizationId = action.payload?.organizationId ?? null
        state.startDate = action.payload?.startDate ?? null
        state.endDate = action.payload?.endDate ?? null
        state.fetchedFetchKey = action.payload?.fetchKey ?? null
      })
      .addCase(fetchOrganizationPagePropertyOutliers.rejected, (state, action) => {
        if (pagePropertyOutliersKey(action.meta.arg || {}) !== String(state.requestedFetchKey ?? "")) {
          return
        }
        state.status = "failed"
        state.error = action.payload ?? "Failed to fetch organization property outliers"
        state.data = null
        state.fetchedFetchKey = state.requestedFetchKey
      })
  },
})

export const { clearOrganizationPagePropertyOutliers } = organizationPagePropertyOutliersSlice.actions
export default organizationPagePropertyOutliersSlice.reducer
